var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Autosizer, Protect, Scrollbar } from 'app/components';
import { useLocalStorage, useSingleAndDoubleClick } from 'app/services';
import React, { createContext, createRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
const TableContext = createContext(null);
const Table = ({ columns: inputColumns, customColumnsRender = null, showColumns, rows, searchValue, searchValueKeys = [], checkedRows = [], selectedRow = null, onRowClick = () => { }, onRowDoubleClick = () => { }, onSortChange = () => { }, sortSelectedKey: controlledSortSelectedKey, manualSort = false, hasMore = false, isLoadingMore = false, onLoadMore = () => { }, storageColumnsWidthKey, itemSize = 24, stickyIndices = [0], overscanCount = 10, }) => {
    const isResizingColumn = useRef(null);
    const [sortSelectedKey, setSortSelectedKey] = useState(controlledSortSelectedKey !== null && controlledSortSelectedKey !== void 0 ? controlledSortSelectedKey : {
        name: inputColumns[0].name,
        order: 'DESC',
    });
    const [columnsWidth, setColumnsWidth] = useLocalStorage(storageColumnsWidthKey, {});
    const { handleItemClick: onClick, handleItemDoubleClick: onDoubleClick } = useSingleAndDoubleClick({
        onClick: (v) => onRowClick(v),
        onDoubleClick: (v) => onRowDoubleClick(v),
        timeout: 0,
    });
    const columns = useMemo(() => inputColumns
        .filter((column) => showColumns.indexOf(column.name) !== -1)
        .map((column) => (Object.assign(Object.assign({}, column), { width: columnsWidth[column.name] ? columnsWidth[column.name] : column.width ? column.width : column.minWidth }))), [inputColumns, columnsWidth, showColumns]);
    const columnsWatch = useRef(columns);
    const columnsRefs = useRef(columns.reduce((acc, column) => {
        acc[column.name] = createRef();
        return acc;
    }, {}));
    const sortRows = useMemo(() => {
        const compareFn = (_a, _b) => {
            const a = _a[sortSelectedKey.name];
            const b = _b[sortSelectedKey.name];
            switch (typeof a) {
                case 'string':
                    if (/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01]) ([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/.test(a)) {
                        switch (sortSelectedKey.order) {
                            case 'ASC':
                                return new Date(a).getTime() - new Date(b).getTime();
                            case 'DESC':
                                return new Date(b).getTime() - new Date(a).getTime();
                        }
                    }
                    else {
                        switch (sortSelectedKey.order) {
                            case 'ASC':
                                return a.localeCompare(b);
                            case 'DESC':
                                return b.localeCompare(a);
                        }
                    }
                    break;
                case 'boolean':
                case 'number': {
                    const numA = Number(a);
                    const numB = Number(b);
                    switch (sortSelectedKey.order) {
                        case 'ASC':
                            return numA - numB;
                        case 'DESC':
                            return numB - numA;
                    }
                    break;
                }
                default:
                    break;
            }
            return 0;
        };
        const visibleRows = searchValue.length
            ? rows.filter((row) => searchValueKeys.some((key) => { var _c; return ((_c = row[key]) === null || _c === void 0 ? void 0 : _c.toLowerCase().indexOf(searchValue.toLowerCase())) !== -1; }))
            : rows;
        return manualSort ? visibleRows : visibleRows.sort(compareFn);
    }, [rows, searchValue, sortSelectedKey, searchValueKeys, manualSort]);
    const adjustWidthColumn = (name, width) => {
        var _c, _d, _e, _f, _g;
        const columnIndex = columnsWatch.current.findIndex((column) => column.name === name);
        const column = columnsWatch.current[columnIndex];
        const newWidth = width > column.maxWidth ? column.maxWidth : width < column.minWidth ? column.minWidth : width;
        const ul = (_e = (_d = (_c = columnsRefs.current[name].current) === null || _c === void 0 ? void 0 : _c.parentElement) === null || _d === void 0 ? void 0 : _d.parentElement) === null || _e === void 0 ? void 0 : _e.parentElement;
        const parentElement = (_f = columnsRefs.current[name].current) === null || _f === void 0 ? void 0 : _f.parentElement;
        if (parentElement) {
            parentElement.style.width = newWidth + 'px';
            parentElement.style.minWidth = newWidth + 'px';
        }
        for (let i = 0; i < (((_g = ul === null || ul === void 0 ? void 0 : ul.children) === null || _g === void 0 ? void 0 : _g.length) || 0); i++) {
            const child = ul.children[i];
            const cell = child.children[columnIndex];
            cell.style.width = newWidth + 'px';
            cell.style.minWidth = newWidth + 'px';
        }
    };
    const handleOnMouseMove = (e) => {
        var _c, _d;
        if (isResizingColumn.current) {
            const columnRef = (_d = (_c = columnsRefs.current[isResizingColumn.current]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.parentElement;
            const newWidth = e.clientX - ((columnRef === null || columnRef === void 0 ? void 0 : columnRef.getBoundingClientRect().left) || 0);
            adjustWidthColumn(isResizingColumn.current, newWidth);
        }
    };
    const handleOnMouseUp = () => {
        isResizingColumn.current = null;
        const columnsInfo = {};
        columns.forEach((column) => {
            var _c, _d;
            const parentElement = (_d = (_c = columnsRefs.current[column.name]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.parentElement;
            columnsInfo[column.name] = parentElement ? parseInt(parentElement.style.width.slice(0, -2), 10) : column.width;
        });
        setColumnsWidth(columnsInfo);
    };
    const onClickResizeColumn = (column, e) => {
        e.preventDefault();
        isResizingColumn.current = column.name;
    };
    const onColumnRef = (column, el) => {
        if (el && !columnsRefs.current[column.name].current) {
            columnsRefs.current[column.name].current = el;
        }
    };
    const handleSortClick = (column) => {
        const sortData = {
            name: column.name,
            order: sortSelectedKey.name !== column.name ? column.order : sortSelectedKey.order === 'ASC' ? 'DESC' : 'ASC',
        };
        setSortSelectedKey(sortData);
        onSortChange(sortData);
    };
    const handleItemsRendered = ({ visibleStopIndex }) => {
        if (hasMore && !isLoadingMore && visibleStopIndex >= sortRows.length - overscanCount - 1) {
            onLoadMore();
        }
    };
    useEffect(() => {
        document.onmousemove = handleOnMouseMove;
        document.onmouseup = handleOnMouseUp;
        return () => {
            document.onmousemove = null;
            document.onmouseup = null;
        };
    }, []);
    useEffect(() => {
        columnsWatch.current = columns;
    }, [columns]);
    useEffect(() => {
        if (controlledSortSelectedKey) {
            setSortSelectedKey(controlledSortSelectedKey);
        }
    }, [controlledSortSelectedKey]);
    return (React.createElement(Box, { component: 'div', sx: {
            width: '100%',
            height: '100%',
            '*:focus': {
                outline: 'none',
            },
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-container': {
                maxHeight: 'none',
                '.simplebar-horizontal': {
                    right: '0px',
                },
                '.simplebar-vertical': {
                    top: '30px',
                    bottom: '0px',
                },
            },
        } },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(TableContext.Provider, { value: {
                    rows: sortRows,
                    width,
                    height,
                    columns,
                    columnsRefs: columnsRefs.current,
                    checkedRows,
                    searchValue,
                    sortSelectedKey,
                    onSortClick: handleSortClick,
                    onCheckboxClick: () => { },
                    onClickResizeColumn,
                    onColumnRef,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => {
                    var _c, _d;
                    return (React.createElement(FixedSizeList, { height: height, innerElementType: InnerElementType, itemCount: sortRows.length, itemSize: itemSize, stickyIndices: stickyIndices, overscanCount: overscanCount, onItemsRendered: handleItemsRendered, innerRef: contentNodeRef, outerRef: scrollableNodeRef, itemData: {
                            rows: sortRows,
                            itemWidth: (_d = (_c = scrollableNodeRef === null || scrollableNodeRef === void 0 ? void 0 : scrollableNodeRef.current) === null || _c === void 0 ? void 0 : _c.scrollWidth) !== null && _d !== void 0 ? _d : width,
                            columns,
                            selectedRow,
                            onClick,
                            onDoubleClick,
                            onColumnRef,
                            customColumnsRender,
                        } }, RowRender));
                })))))));
};
const InnerElementType = forwardRef((_c, ref) => {
    var { children, style } = _c, rest = __rest(_c, ["children", "style"]);
    return (React.createElement(TableContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { onSortClick, onClickResizeColumn, sortSelectedKey, columns, onColumnRef } = context;
        return (React.createElement(List, Object.assign({ ref: ref, style: Object.assign(Object.assign({}, style), { minHeight: '100%' }) }, rest),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: style === null || style === void 0 ? void 0 : style.height,
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) }, columns.map((column) => (React.createElement(Protect, { key: column.name, permissions: column.permissions || [], renderPermitted: () => (React.createElement(ListItemText, { style: {
                        minWidth: column.width ? column.width : column.minWidth,
                    } })), renderForbidden: () => React.createElement(React.Fragment, null) })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    position: 'sticky',
                    top: '0',
                    width: '100%',
                    display: 'flex',
                    zIndex: '2',
                    '& > .MuiListItemButton-root': {
                        '&:first-of-type': {
                            '.MuiListItemText-root': {
                                paddingLeft: '31px',
                            },
                        },
                    },
                    '& > .MuiBox-root': {
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                    },
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        height: '30px',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            svg: {
                                fontSize: '14px',
                            },
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                            '.MuiCheckbox-root': {
                                padding: theme.spacing(0),
                                marginLeft: '-2px',
                                svg: {
                                    fontSize: '18px',
                                },
                            },
                        },
                    },
                }) }, columns.map((column) => (React.createElement(Protect, { key: column.name, permissions: column.permissions || [], renderForbidden: () => React.createElement(React.Fragment, null), renderPermitted: () => (React.createElement(Box, { component: 'div', sx: {
                        display: 'flex',
                        position: 'relative',
                        minWidth: column.width ? column.width : column.minWidth,
                    } },
                    React.createElement(ListItemButton, { onClick: () => onSortClick(column), disableTouchRipple: !column.order, sx: (theme) => ({
                            flex: '1 1 auto !important',
                            '.MuiTypography-root': {
                                width: 'auto',
                                '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, (sortSelectedKey.name === column.name && Object.assign({ opacity: '1' }, (sortSelectedKey.order === 'DESC' && {
                                    transform: 'rotateZ(180deg)',
                                })))),
                            },
                        }) }, column.name == 'download' ? (React.createElement(ListItemText, { disableTypography: true },
                        React.createElement(Typography, null, column.label))) : (React.createElement(ListItemText, { disableTypography: true },
                        React.createElement(Typography, null, column.label),
                        React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))),
                    React.createElement(Box, { component: 'div', ref: (el) => onColumnRef(column, el), onMouseDown: (e) => onClickResizeColumn(column, e), sx: (theme) => ({
                            minWidth: '4px',
                            minHeight: '30px',
                            cursor: 'col-resize',
                            backgroundColor: theme.palette.background.default,
                            '&:hover, &:active': {
                                backgroundColor: alpha(theme.palette.text.secondary, 0.12),
                            },
                        }) }))) })))),
            children));
    }));
});
InnerElementType.displayName = 'InnerElementType';
const RowRender = ({ data, index, style }) => {
    const { rows, selectedRow, onClick, onDoubleClick, columns, customColumnsRender } = data;
    return (React.createElement(ListItem, { key: index, style: style, selected: (selectedRow === null || selectedRow === void 0 ? void 0 : selectedRow.id) === rows[index].id, onClick: () => onClick(rows[index]), onDoubleClick: () => onDoubleClick(rows[index]), disablePadding: true, sx: (theme) => (Object.assign({ width: '100%', marginTop: '30px', userSelect: 'none', borderRadius: '0', '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiCheckbox-root': {
                    color: theme.palette.primary.contrastText,
                    '&.Mui-checked': {
                        color: theme.palette.primary.contrastText,
                    },
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'center',
                overflow: 'hidden',
                '&:first-of-type': {
                    paddingLeft: theme.spacing(2),
                    gap: '3px',
                    svg: {
                        fontSize: '16px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
                '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                        fontSize: '18px',
                    },
                },
            } }, ((rows.length - (rows.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) }, columns.map((column) => customColumnsRender ? (customColumnsRender(column, rows[index])) : (React.createElement(ListItemText, { key: column.name, sx: { width: column.width } },
        React.createElement(Typography, null, column.format ? column.format(rows[index][column.name]) : rows[index][column.name]))))));
};
export default Table;
